import './aui-docs-search.less';
import $ from 'jquery';
import fuzzysort from 'fuzzysort';
import sitenavData from '../../../../partials/sitenav-data';
import '../../../../../core/src/js/aui/select2';

const TRIGGER_SELECTOR = '[data-aui-docs-search-trigger]';
const INPUT_ID = 'aui-docs-search';

// In production each docs version is served under a `/aui/<version>/` prefix
// (e.g. `/aui/latest/`, `/aui/11.0/`). Locally a single version is served from
// the root (`/`), with no such prefix.
const VERSION_PREFIX_SEGMENT = 'aui';

/**
 * Determine the absolute documentation root for the current environment.
 *
 * The quick search works out where it is from the current URL: in production
 * the first two path segments form the version prefix (`/aui/<version>/`), so
 * search results must be absolute paths under that prefix.
 *
 * @returns {string} Absolute docs root, e.g. `/aui/latest/` in production or `/` locally.
 */
function getDocsRoot() {
    const segments = window.location.pathname.split('/').filter(Boolean);
    if (segments[0] === VERSION_PREFIX_SEGMENT && segments.length >= 2) {
        return `/${segments[0]}/${segments[1]}/`;
    }
    return '/';
}

const docsRoot = getDocsRoot();

const data = sitenavData.map(({ group, items }) => ({
    text: group,
    children: items.map(({ path, label }) => ({ id: docsRoot + path, text: label })),
}));

function init() {
    const trigger = document.querySelector(TRIGGER_SELECTOR);
    if (!trigger) {
        return;
    }

    // Show ⌘K on Mac, Ctrl+K elsewhere.
    const isMac =
        (navigator.userAgentData && navigator.userAgentData.platform === 'macOS') ||
        /Mac/i.test(navigator.platform);
    const kbdEl = trigger.querySelector('.aui-docs-search-trigger__kbd');
    if (kbdEl && isMac) {
        kbdEl.textContent = '⌘K';
    }

    const $select = $('#' + INPUT_ID);
    if (!$select.length) {
        return;
    }

    // Anchor the dropdown under the trigger (the Select2 container is hidden
    // off-screen). Merged via $.extend after Select2's own positioning math.
    const dropdownCss = () => {
        const rect = trigger.getBoundingClientRect();
        const desiredWidth = Math.max(rect.width, 320);
        const viewportPad = 12;
        const maxLeft = window.innerWidth - desiredWidth - viewportPad;
        let left = rect.right + window.scrollX - desiredWidth;
        left = Math.max(viewportPad, Math.min(left, maxLeft));
        return { top: rect.bottom + window.scrollY, left, width: desiredWidth };
    };

    let initialized = false;

    const open = () => {
        if (!initialized) {
            $select.auiSelect2({
                placeholder: 'Search docs…',
                minimumInputLength: 0,
                dropdownAutoWidth: false,
                data,
                matcher: (term, text) => !term || fuzzysort.single(term, text) !== null,
                // Bold matched chars (incl. scattered ones) via fuzzysort's <b>…</b> highlighter.
                formatResult: (item, _container, { term }) => {
                    const match = item.id && term && fuzzysort.single(term, item.text);
                    return match ? match.highlight() : item.text;
                },
                escapeMarkup: (m) => m,
                containerCssClass: 'aui-docs-search__container',
                dropdownCssClass: 'aui-docs-search__dropdown',
                dropdownCss,
            });
            $select.on('change', () => {
                const href = $select.val();
                if (href) {
                    window.location.href = href;
                }
            });
            initialized = true;
        }
        $select.auiSelect2('open');
    };

    trigger.addEventListener('click', open);
    document.addEventListener('keydown', (event) => {
        if ((event.metaKey || event.ctrlKey) && event.key && event.key.toLowerCase() === 'k') {
            event.preventDefault();
            open();
        }
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
